/* =========================================================================
 * IBC 2.0 — THE MAKING (ibc-craft)
 * -------------------------------------------------------------------------
 * The image-backed carousel behind sections/ibc-craft.liquid.
 *
 *   [data-ibc-craft]           the section, carrying data-ibc-craft-count
 *   [data-ibc-craft-track]     the native scroller
 *   [data-ibc-craft-slide]     one frame; the section renders THREE passes
 *                              over the photographs, so there are 3N of
 *                              them and the middle pass is the real one
 *   [data-ibc-craft-pager]     prev / pips / next / pause, `hidden` until
 *                              this file decides the loop is worth having.
 *                              All four exist at every width and this file
 *                              binds to all four; below 900px the arrows
 *                              are `display: none` and the pause control is
 *                              clipped-but-focusable, both in
 *                              assets/ibc-craft.css, so a phone is offered
 *                              the pips alone. Nothing here is
 *                              breakpoint-aware except `perView()`.
 *   [data-ibc-craft-live]      polite announcement, manual moves only
 *
 * WHY A NATIVE SCROLLER AND NOT A TRANSFORM TRACK
 * -----------------------------------------------
 * A transform track would need a hand-rolled drag, and a hand-rolled drag
 * has to decide on every touchmove whether the gesture is horizontal or
 * vertical — which is exactly how carousels end up hijacking the page
 * scroll. `overflow-x: auto` with `scroll-snap-type: x mandatory` gives the
 * browser that decision back, for free, and it is the pattern the rest of
 * this theme already uses (ibc-tile.js, ibc-shops, the intro carousel).
 * This file only listens.
 *
 * HOW THE LOOP IS SEAMLESS
 * ------------------------
 * The section renders the N photographs three times over: a clone pass, the
 * real pass, a clone pass. At rest the track is always parked somewhere in
 * the MIDDLE pass, i.e. at a slide index in [N, 2N). One advance can take
 * it to 2N at most, and a swipe can take it further; whenever the track
 * comes to rest outside that window, `normalise()` puts it back by setting
 * `scrollLeft` to the congruent index — index j where j ≡ i (mod N).
 *
 * That reposition is invisible for a reason that is worth stating plainly:
 * slide j and slide i hold the SAME photograph, and so does every slide
 * after them, because the sequence is periodic with period N and there is a
 * whole pass of headroom on each side. The pixels before and after the jump
 * are identical, and it only ever happens with the track at rest. There is
 * no jump back to the start because there is no start.
 *
 * WHY NO PHOTOGRAPH IS EVER ON SCREEN TWICE
 * -----------------------------------------
 * With `per` frames visible, a moving track shows at most `per + 1`
 * CONSECUTIVE slides at any instant — the `per` it is arriving at plus the
 * one it is leaving. Consecutive slides map to consecutive source indices
 * mod N, and `per + 1` consecutive values mod N are all distinct if and
 * only if N >= per + 1. So the loop is only ever switched on when
 *
 *     count >= perView() + 1
 *
 * On a phone `per` is 1, so two photographs are enough. On desktop `per` is
 * 3, so FOUR are needed: with exactly three, a 3-up window advancing by one
 * would have to show the leaving frame re-entering on the other side, and
 * that is the one thing this carousel is not allowed to do. Three
 * photographs therefore give a still three-column stage on desktop and a
 * proper carousel on a phone, and the section's schema says so.
 *
 * NO DUPLICATE TIMERS WHEN THE EDITOR RELOADS THE SECTION
 * ------------------------------------------------------
 * `window.IbcCraft` is the re-entry guard, the same idiom as
 * assets/ibc-tile.js: if the constant is already there the file returns
 * immediately, so re-executing the tag Shopify re-inserts cannot register a
 * second set of document listeners. Each section root is mounted once
 * (`root.__ibcCraft`), and `shopify:section:unload` tears its instance
 * down completely — interval, resize and settle timers, the intersection
 * observer, and every listener this file added.
 * ====================================================================== */
(function () {
  'use strict';

  if (window.IbcCraft) return;

  /* Between five and seven seconds. Slow enough to read a frame, quiet
     enough not to feel like it is demanding attention. */
  var AUTO_MS = 6000;
  /* The track has stopped moving. Long enough to survive a smooth scroll's
     last few frames, short enough to feel immediate. */
  var SETTLE_MS = 120;
  /* After a swipe, a pip or an arrow, how long the section sits COMPLETELY
     still before it starts a fresh interval. It is not an alternative to
     AUTO_MS, it is added to it: the first automatic move after a manual one
     is RESUME_MS + AUTO_MS = 8.6s away, which is the point — see nudge(). */
  var RESUME_MS = 2600;
  var RESIZE_MS = 150;

  var mounted = [];

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function mq(query) {
    return window.matchMedia ? window.matchMedia(query) : null;
  }

  function listen(media, fn) {
    if (!media) return;
    if (media.addEventListener) media.addEventListener('change', fn);
    else if (media.addListener) media.addListener(fn);
  }

  function unlisten(media, fn) {
    if (!media) return;
    if (media.removeEventListener) media.removeEventListener('change', fn);
    else if (media.removeListener) media.removeListener(fn);
  }

  function still(st) {
    return !!(st.motion && st.motion.matches);
  }

  /* ------------------------------------------------------------ geometry -- */

  /* The pitch between two frames, read from the DOM rather than computed
     from a percentage, so rounding cannot drift over a long loop.
     `offsetLeft` is a layout position and is unaffected by `scrollLeft`. */
  function pitch(st) {
    if (st.slides.length > 1) {
      var step = st.slides[1].offsetLeft - st.slides[0].offsetLeft;
      if (step > 0) return step;
    }
    var per = perView(st);
    var width = st.track.clientWidth || 0;
    return per > 0 ? width / per : width;
  }

  function perView(st) {
    if (st.phone && st.phone.matches) return 1;
    return Math.min(3, st.count);
  }

  function indexNow(st) {
    var step = pitch(st);
    if (step <= 0) return st.count;
    return Math.round(st.track.scrollLeft / step);
  }

  function sourceOf(st, index) {
    var n = st.count;
    if (n < 1) return 0;
    return ((index % n) + n) % n;
  }

  /* --------------------------------------------------------------- moves -- */

  function goTo(st, index, smooth, announce) {
    var step = pitch(st);
    if (step <= 0) return;

    if (!st.loop) index = st.count;
    st.announce = !!announce;

    var left = index * step;
    if (smooth && !still(st) && typeof st.track.scrollTo === 'function') {
      try {
        st.track.scrollTo({ left: left, behavior: 'smooth' });
        return;
      } catch (error) {
        /* fall through to the instant placement below */
      }
    }
    st.track.scrollLeft = left;
    settled(st);
  }

  function advance(st, direction, announce) {
    if (!st.loop) return;
    goTo(st, indexNow(st) + direction, true, announce);
  }

  /* Jump to a chosen photograph by the shortest route. There is a full pass
     of clones on each side, so the same source is reachable at three
     positions and the nearest one is always in range. */
  function goToSource(st, source, announce) {
    if (!st.loop) return;
    var n = st.count;
    var here = indexNow(st);
    var best = n + source;
    var bestGap = Math.abs(best - here);
    var options = [source, n + source, n + n + source];
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      if (option < 0 || option > st.slides.length - perView(st)) continue;
      var gap = Math.abs(option - here);
      if (gap < bestGap) {
        best = option;
        bestGap = gap;
      }
    }
    goTo(st, best, true, announce);
  }

  /* The whole loop, in four lines. Called only with the track at rest. */
  function normalise(st) {
    if (!st.loop) return;
    var step = pitch(st);
    if (step <= 0) return;
    var here = Math.round(st.track.scrollLeft / step);
    var home = st.count + sourceOf(st, here);
    if (home !== here) st.track.scrollLeft = home * step;
  }

  /* ---------------------------------------------------------------- paint -- */

  function paint(st) {
    var source = sourceOf(st, indexNow(st));
    st.source = source;

    for (var i = 0; i < st.pips.length; i++) {
      var on = i === source;
      st.pips[i].setAttribute('aria-current', on ? 'true' : 'false');
    }

    /* Only manual moves are announced. A polite live region firing every
       six seconds on its own is noise, not help. */
    if (st.live && st.announce) {
      st.live.textContent = 'Photograph ' + (source + 1) + ' of ' + st.count;
      st.announce = false;
    }
  }

  function settled(st) {
    normalise(st);
    paint(st);
  }

  /* ------------------------------------------------------------ autoplay -- */

  function mayPlay(st) {
    return (
      st.loop &&
      st.wanted &&
      !still(st) &&
      st.visible &&
      !st.hover &&
      !st.focus &&
      !st.drag &&
      !document.hidden
    );
  }

  function stopAuto(st) {
    if (st.timer) {
      window.clearInterval(st.timer);
      st.timer = 0;
    }
  }

  function refreshAuto(st) {
    stopAuto(st);
    if (!mayPlay(st)) return;
    st.timer = window.setInterval(function () {
      advance(st, 1, false);
    }, AUTO_MS);
  }

  /* A manual move should not be followed a moment later by an automatic
     one, and it should not stop the section for good either.

     PAUSE-THEN-RESUME, NOT AN INTERVAL RESET. A bare reset would restart
     the six-second clock and give the visitor exactly what they already
     had. This clears the timer, sits still for RESUME_MS, and only then
     starts a fresh interval — so the first automatic move after a swipe or
     a pip is 2.6s + 6s = 8.6s away, comfortably longer than the 6s a reset
     would buy. That matters most on a phone, where there is no visible
     pause button and this is the ONLY quiet the visitor can ask for: a tap
     or a swipe is them saying "I am looking at this one". It still resumes
     by itself, so the section never silently stops and the visitor is
     never left wondering whether they broke it. */
  function nudge(st) {
    stopAuto(st);
    if (st.resume) window.clearTimeout(st.resume);
    st.resume = window.setTimeout(function () {
      st.resume = 0;
      refreshAuto(st);
    }, RESUME_MS);
  }

  function setPlayState(st) {
    if (!st.play) return;
    var playing = st.wanted && !still(st);
    st.play.setAttribute('data-state', playing ? 'playing' : 'paused');
    if (st.playLabel) {
      st.playLabel.textContent = playing
        ? 'Pause the photographs'
        : 'Play the photographs';
    }
    /* With reduced motion asked for, nothing moves by itself and the
       control has nothing to control.

       This line was inert until now: `.ibc-craft__play` is given
       `display: inline-flex` by an author rule, which beats the user
       agent's `[hidden] { display: none }` outright, so the attribute
       landed and nothing happened. ibc-craft.css now carries an explicit
       `.ibc-craft .ibc-craft__play[hidden]` rule and this does what it
       says. */
    st.play.hidden = still(st);
  }

  /* --------------------------------------------------------------- modes -- */

  /* Which shape the section is in right now. Re-run on the 900px change and
     on resize, because `per` — and therefore whether the loop is honest —
     depends on the breakpoint. */
  function applyMode(st) {
    var per = perView(st);
    st.per = per;
    st.loop = st.count >= per + 1;

    st.root.setAttribute('data-ibc-craft-loop', st.loop ? 'on' : 'off');
    if (st.pager) st.pager.hidden = !st.loop;
    setPlayState(st);

    var step = pitch(st);
    if (step > 0) {
      var index = st.loop ? st.count + sourceOf(st, st.source) : st.count;
      st.track.scrollLeft = index * step;
      /* A section that was `display: none` when it mounted — a theme editor
         preview being built, a tab that has not been opened — measures zero
         and cannot be placed yet. This records that it has been. */
      st.placed = true;
    }

    paint(st);
    refreshAuto(st);
  }

  /* ---------------------------------------------------------------- mount -- */

  function mount(root) {
    if (!root || root.__ibcCraft) return;

    var track = root.querySelector('[data-ibc-craft-track]');
    if (!track) return;

    var count = parseInt(root.getAttribute('data-ibc-craft-count'), 10);
    if (!count || count < 1) return;

    var slides = slice(track.querySelectorAll('[data-ibc-craft-slide]'));
    /* The three-pass contract is what the whole loop rests on. If the
       markup ever stops honouring it, do nothing rather than something
       wrong: the CSS alone still gives a correct still stage. */
    if (slides.length !== count * 3) return;

    var pager = root.querySelector('[data-ibc-craft-pager]');
    var play = root.querySelector('[data-ibc-craft-play]');

    var st = {
      root: root,
      track: track,
      slides: slides,
      images: slice(track.querySelectorAll('img')),
      count: count,
      pager: pager,
      prev: root.querySelector('[data-ibc-craft-prev]'),
      next: root.querySelector('[data-ibc-craft-next]'),
      pips: slice(root.querySelectorAll('[data-ibc-craft-pip]')),
      play: play,
      playLabel: root.querySelector('[data-ibc-craft-play-label]'),
      live: root.querySelector('[data-ibc-craft-live]'),
      phone: mq('(max-width: 899px)'),
      motion: mq('(prefers-reduced-motion: reduce)'),
      sectionId: root.getAttribute('data-ibc-craft') || '',
      per: 1,
      loop: false,
      source: 0,
      wanted: true,
      visible: false,
      hover: false,
      focus: false,
      drag: false,
      warmed: false,
      placed: false,
      announce: false,
      timer: 0,
      resume: 0,
      settleTimer: 0,
      resizeTimer: 0,
      observer: null
    };

    root.__ibcCraft = st;
    root.setAttribute('data-ibc-craft-js', 'true');

    /* ------------------------------------------------------- handlers -- */

    st.onScroll = function () {
      window.clearTimeout(st.settleTimer);
      st.settleTimer = window.setTimeout(function () {
        settled(st);
      }, SETTLE_MS);
    };

    st.onEnter = function () {
      st.hover = true;
      refreshAuto(st);
    };

    st.onLeave = function () {
      st.hover = false;
      refreshAuto(st);
    };

    /* The keyboard visitor's first line of defence, and the one that does
       not depend on a control being on screen: focus landing ANYWHERE in
       the section — a pip, the scrollable track, the CTA — stops the
       autoplay outright for as long as it stays there. Below 900px, where
       the pause button is clipped, this is what a Tab press buys before
       the visitor has even reached it; the button is then what makes the
       stop outlast their focus. */
    st.onFocusIn = function () {
      st.focus = true;
      refreshAuto(st);
    };

    st.onFocusOut = function (event) {
      /* Focus moving between two controls inside the section is not focus
         leaving it. */
      if (event && event.relatedTarget && root.contains(event.relatedTarget)) return;
      st.focus = false;
      refreshAuto(st);
    };

    st.onPointerDown = function () {
      st.drag = true;
      stopAuto(st);
    };

    st.onPointerUp = function () {
      if (!st.drag) return;
      st.drag = false;
      nudge(st);
    };

    st.onPrev = function () {
      nudge(st);
      advance(st, -1, true);
    };

    st.onNext = function () {
      nudge(st);
      advance(st, 1, true);
    };

    st.onPip = function (event) {
      var index = parseInt(event.currentTarget.getAttribute('data-ibc-craft-pip'), 10);
      if (isNaN(index)) return;
      nudge(st);
      goToSource(st, index, true);
    };

    st.onPlay = function () {
      st.wanted = !st.wanted;
      setPlayState(st);
      if (st.resume) {
        window.clearTimeout(st.resume);
        st.resume = 0;
      }
      refreshAuto(st);
    };

    st.onPagerKey = function (event) {
      var key = event.key;
      var handled = true;
      if (key === 'ArrowRight') advance(st, 1, true);
      else if (key === 'ArrowLeft') advance(st, -1, true);
      else if (key === 'Home') goToSource(st, 0, true);
      else if (key === 'End') goToSource(st, st.count - 1, true);
      else handled = false;
      if (!handled) return;
      event.preventDefault();
      nudge(st);
    };

    st.onResize = function () {
      window.clearTimeout(st.resizeTimer);
      st.resizeTimer = window.setTimeout(function () {
        applyMode(st);
      }, RESIZE_MS);
    };

    st.onMedia = function () {
      applyMode(st);
    };

    /* A photograph parked off to the right of a horizontal scroller is not
       intersecting the viewport, so a lazy one can still be unfetched when
       the track reaches it. Once the section itself is on screen the whole
       band is wanted; flipping `loading` from lazy to eager starts the
       fetch immediately. */
    st.warm = function () {
      if (st.warmed) return;
      st.warmed = true;
      for (var i = 0; i < st.images.length; i++) {
        if (st.images[i].loading === 'lazy') st.images[i].loading = 'eager';
      }
    };

    /* -------------------------------------------------------- wiring -- */

    track.addEventListener('scroll', st.onScroll);
    track.addEventListener('pointerdown', st.onPointerDown);
    document.addEventListener('pointerup', st.onPointerUp);
    document.addEventListener('pointercancel', st.onPointerUp);
    root.addEventListener('mouseenter', st.onEnter);
    root.addEventListener('mouseleave', st.onLeave);
    root.addEventListener('focusin', st.onFocusIn);
    root.addEventListener('focusout', st.onFocusOut);
    if (st.prev) st.prev.addEventListener('click', st.onPrev);
    if (st.next) st.next.addEventListener('click', st.onNext);
    if (st.play) st.play.addEventListener('click', st.onPlay);
    for (var p = 0; p < st.pips.length; p++) {
      st.pips[p].addEventListener('click', st.onPip);
    }
    if (pager) pager.addEventListener('keydown', st.onPagerKey);
    window.addEventListener('resize', st.onResize);
    listen(st.phone, st.onMedia);
    listen(st.motion, st.onMedia);

    if ('IntersectionObserver' in window) {
      st.observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].target !== root) continue;
            st.visible = entries[i].isIntersecting;
            if (!st.visible) {
              refreshAuto(st);
              continue;
            }
            st.warm();
            /* If the section could not be measured at mount, this is the
               first moment it certainly can be. Place it once, then leave
               the visitor's position alone for every later crossing. */
            if (!st.placed) applyMode(st);
            else refreshAuto(st);
          }
        },
        { threshold: 0.2 }
      );
      st.observer.observe(root);
    } else {
      st.visible = true;
      st.warm();
    }

    mounted.push(st);
    applyMode(st);
  }

  /* -------------------------------------------------------------- unmount -- */

  function destroy(root) {
    if (!root) return;
    var st = root.__ibcCraft;
    if (!st) return;

    stopAuto(st);
    window.clearTimeout(st.resume);
    window.clearTimeout(st.settleTimer);
    window.clearTimeout(st.resizeTimer);
    if (st.observer) st.observer.disconnect();

    st.track.removeEventListener('scroll', st.onScroll);
    st.track.removeEventListener('pointerdown', st.onPointerDown);
    document.removeEventListener('pointerup', st.onPointerUp);
    document.removeEventListener('pointercancel', st.onPointerUp);
    root.removeEventListener('mouseenter', st.onEnter);
    root.removeEventListener('mouseleave', st.onLeave);
    root.removeEventListener('focusin', st.onFocusIn);
    root.removeEventListener('focusout', st.onFocusOut);
    if (st.prev) st.prev.removeEventListener('click', st.onPrev);
    if (st.next) st.next.removeEventListener('click', st.onNext);
    if (st.play) st.play.removeEventListener('click', st.onPlay);
    for (var p = 0; p < st.pips.length; p++) {
      st.pips[p].removeEventListener('click', st.onPip);
    }
    if (st.pager) st.pager.removeEventListener('keydown', st.onPagerKey);
    window.removeEventListener('resize', st.onResize);
    unlisten(st.phone, st.onMedia);
    unlisten(st.motion, st.onMedia);

    var at = mounted.indexOf(st);
    if (at > -1) mounted.splice(at, 1);

    root.removeAttribute('data-ibc-craft-js');
    root.removeAttribute('data-ibc-craft-loop');
    if (st.pager) st.pager.hidden = true;
    root.__ibcCraft = null;
  }

  function scan(scope) {
    var roots = slice((scope || document).querySelectorAll('[data-ibc-craft]'));
    for (var i = 0; i < roots.length; i++) mount(roots[i]);
  }

  /* -------------------------------------------------------------- the doc -- */

  /* Claimed BEFORE anything below runs. If a first pass over the document
     were to throw, the constant would otherwise still be unset and the next
     re-insertion of the tag would sail past the guard and register a second
     set of document listeners — the exact duplication this exists to stop. */
  window.IbcCraft = {
    mount: mount,
    destroy: destroy,
    scan: scan
  };

  /* Registered exactly once, because the guard at the top of this file
     stops a re-inserted <script> tag getting this far a second time. */
  document.addEventListener('shopify:section:load', function (event) {
    var scope = event && event.target ? event.target : document;
    scan(scope);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event && event.target ? event.target : null;
    if (!scope || !scope.querySelectorAll) return;
    var roots = slice(scope.querySelectorAll('[data-ibc-craft]'));
    for (var i = 0; i < roots.length; i++) destroy(roots[i]);
  });

  document.addEventListener('visibilitychange', function () {
    for (var i = 0; i < mounted.length; i++) refreshAuto(mounted[i]);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
})();
